import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Trash2, Plus, GripVertical } from "lucide-react";

interface Stage {
  id: string;
  name: string;
  order_number: number;
  color: string | null;
}

interface StageDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  stages: Stage[];
  onSuccess: () => void;
}

export function StageDialog({
  open,
  onOpenChange,
  stages,
  onSuccess,
}: StageDialogProps) {
  const { toast } = useToast();
  const [newStageName, setNewStageName] = useState("");
  const [newStageColor, setNewStageColor] = useState("#4ECDC4");
  const [loading, setLoading] = useState(false);

  const handleAddStage = async () => {
    if (!newStageName.trim()) return;

    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const maxOrder = Math.max(...stages.map(s => s.order_number), 0);

      const { error } = await supabase.from("pipeline_stages").insert([{
        name: newStageName,
        order_number: maxOrder + 1,
        color: newStageColor,
        user_id: user.id,
      }]);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Stage added successfully",
      });

      setNewStageName("");
      setNewStageColor("#4ECDC4");
      onSuccess();
    } catch (error) {
      console.error("Error adding stage:", error);
      toast({
        title: "Error",
        description: "Failed to add stage",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteStage = async (stageId: string) => {
    if (!confirm("Are you sure? This will delete all deals in this stage.")) return;

    try {
      const { error } = await supabase
        .from("pipeline_stages")
        .delete()
        .eq("id", stageId);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Stage deleted successfully",
      });

      onSuccess();
    } catch (error) {
      console.error("Error deleting stage:", error);
      toast({
        title: "Error",
        description: "Failed to delete stage",
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Manage Pipeline Stages</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          <div className="space-y-4">
            <h3 className="font-semibold">Current Stages</h3>
            <div className="space-y-2">
              {stages.map((stage) => (
                <div
                  key={stage.id}
                  className="flex items-center gap-3 p-3 border rounded-lg"
                >
                  <GripVertical className="h-4 w-4 text-muted-foreground" />
                  {stage.color && (
                    <div
                      className="w-4 h-4 rounded-full flex-shrink-0"
                      style={{ backgroundColor: stage.color }}
                    />
                  )}
                  <span className="flex-1">{stage.name}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteStage(stage.id)}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="font-semibold">Add New Stage</h3>
            <div className="grid gap-4">
              <div>
                <Label htmlFor="stage-name">Stage Name</Label>
                <Input
                  id="stage-name"
                  value={newStageName}
                  onChange={(e) => setNewStageName(e.target.value)}
                  placeholder="e.g., Negotiation"
                />
              </div>
              <div>
                <Label htmlFor="stage-color">Color</Label>
                <div className="flex gap-2">
                  <Input
                    id="stage-color"
                    type="color"
                    value={newStageColor}
                    onChange={(e) => setNewStageColor(e.target.value)}
                    className="w-20 h-10"
                  />
                  <Input
                    value={newStageColor}
                    onChange={(e) => setNewStageColor(e.target.value)}
                    placeholder="#4ECDC4"
                  />
                </div>
              </div>
              <Button
                onClick={handleAddStage}
                disabled={!newStageName.trim() || loading}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Stage
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
